import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FaClock, FaPowerOff, FaBars, FaBell, FaCog } from 'react-icons/fa';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import UserAvatar from './UserAvatar';
import { TITLES } from './TitleSync';
import { API_BASE } from '../utils/api';

const AUTO_LOGOUT_TIME = 30 * 60 * 1000;

export default function Topbar({ user, theme = 'dark', onMenuToggle }) {
  const [time, setTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifMenu, setShowNotifMenu] = useState(false);

  const [timeright, setTimeright] = useState(AUTO_LOGOUT_TIME);
  const [showWarning, setShowWarning] = useState(false);

  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const rootRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const userId = user?.SLIITLeoID ?? null;
  const pageTitle = TITLES[location.pathname] || '';

  const handleLogout = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  }, [navigate]);

  const resetTimer = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    setTimeright(AUTO_LOGOUT_TIME);
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      const startTime = Date.now();
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 5 * 60 * 1000 - elapsed);
        setTimeright(remaining);
        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
          handleLogout();
        }
      }, 1000);

      logoutTimerRef.current = setTimeout(() => {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        handleLogout();
      }, 5 * 60 * 1000);
    }, AUTO_LOGOUT_TIME - 5 * 60 * 1000);
  }, [handleLogout]);

  useEffect(() => {
    const clockTimer = setInterval(() => {
      setTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 1000);
    resetTimer();

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const resetTimerOnActivity = () => resetTimer();
    events.forEach((ev) => window.addEventListener(ev, resetTimerOnActivity));

    return () => {
      clearInterval(clockTimer);
      events.forEach((ev) => window.removeEventListener(ev, resetTimerOnActivity));
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [resetTimer]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setShowUserMenu(false);
        setShowNotifMenu(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const fetchNotifs = async () => {
    if (!userId) return;
    try {
      setLoadingNotifs(true);
      const res = await fetch(`${API_BASE}/notifications.php?mode=list&user_id=${encodeURIComponent(userId)}`, {
        credentials: 'include',
      });
      const data = await res.json();
      setNotifs(Array.isArray(data?.notifications) ? data.notifications : []);
      setUnreadCount(Number.isFinite(data?.unread) ? data.unread : 0);
    } catch (e) {
      console.error('Failed to load notifications', e);
    } finally {
      setLoadingNotifs(false);
    }
  };

  const markAllRead = async () => {
    if (!userId) return;
    try {
      await fetch(`${API_BASE}/notifications.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode: 'mark_all_read', user_id: userId }),
      });
      setNotifs((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (e) {
      console.error(e);
    }
  };

  const markOneRead = async (notificationId) => {
    if (!userId) return;
    try {
      await fetch(`${API_BASE}/notifications.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode: 'mark_read', user_id: userId, notification_id: notificationId } ),
      });
      setNotifs((prev) => prev.map((n) => (n.id === notificationId ? { ...n, is_read: 1 } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60_000);
    return () => clearInterval(id);
  }, [userId]);

  const profileImage = user?.image || '/default-user.png';
  const formatMs = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  };

  return (
    <>
      <header ref={rootRef} className="topbar fixed top-0 left-0 right-0 lg:left-64 z-20">
        <div className="crumbs" style={{ minWidth: 0 }}>
          {onMenuToggle && (
            <button onClick={onMenuToggle} className="icon-btn" style={{ marginRight: 4 }} aria-label="Toggle menu">
              <FaBars size={14} />
            </button>
          )}
          {pageTitle && (
            <span className="hidden sm:flex items-center gap-1.5" style={{ minWidth: 0, overflow: 'hidden' }}>
              <Link to="/home">Leo Club Portal</Link>
              <span>/</span>
              <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pageTitle}</b>
            </span>
          )}
        </div>

        <div className="top-actions">
          {/* time (hidden on very small screens; visible from 640px up, well within the 768px target) */}
          <div className="hidden sm:flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--text-2)' }}>
            <FaClock size={12} />
            <span className="num">{time}</span>
          </div>

          {/* notifications */}
          <div className="relative">
            <button
              onClick={() => {
                setShowNotifMenu((s) => !s);
                setShowUserMenu(false);
                fetchNotifs();
              }}
              className="icon-btn"
              aria-haspopup="menu"
              aria-expanded={showNotifMenu}
            >
              <FaBell size={14} />
              {unreadCount > 0 && (
                <span
                  className="num"
                  style={{
                    position: 'absolute', top: -3, right: -3, minWidth: 15, height: 15, padding: '0 3px',
                    borderRadius: 999, background: 'var(--accent-dark)', color: '#fff',
                    fontSize: 9.5, fontWeight: 700, display: 'grid', placeItems: 'center',
                    boxShadow: '0 0 0 2px var(--header-bg)',
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifMenu && (
              <div
                role="menu"
                className="panel absolute right-0 mt-1 w-64 max-h-[60vh] overflow-hidden z-[60]"
                style={{ boxShadow: '0 20px 50px -20px rgba(0,0,0,.7)' }}
              >
                <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                  <div className="text-xs font-semibold">Notifications</div>
                  <button onClick={markAllRead} className="chip" style={{ fontSize: 10.5 }}>
                    Mark all
                  </button>
                </div>

                <div className="max-h-[48vh] overflow-auto">
                  {loadingNotifs && <div className="p-3 text-xs" style={{ color: 'var(--text-2)' }}>Loading…</div>}
                  {!loadingNotifs && notifs.length === 0 && (
                    <div className="p-3 text-xs" style={{ color: 'var(--text-2)' }}>No notifications</div>
                  )}
                  {!loadingNotifs &&
                    notifs.map((n) => (
                      <div key={n.id} className="px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                        <div className="flex items-start gap-2">
                          <div
                            className="mt-1 rounded-full"
                            style={{ width: 8, height: 8, flexShrink: 0, background: n.is_read ? 'var(--surface-3)' : 'var(--accent)' }}
                          />
                          <div className="flex-1">
                            <div className="text-xs font-medium">{n.title}</div>
                            <div className="mt-0.5" style={{ fontSize: 11, color: 'var(--text-2)' }}>{n.message}</div>
                            <div className="mt-1 num" style={{ fontSize: 10, color: 'var(--text-3)' }}>{n.created_at}</div>
                            <div className="mt-1.5 flex gap-1.5">
                              {!n.is_read && (
                                <button onClick={() => markOneRead(n.id)} className="chip" style={{ fontSize: 10 }}>
                                  Read
                                </button>
                              )}
                              {n.link && (
                                <a href={n.link} className="chip" style={{ fontSize: 10 }}>
                                  Open
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* settings */}
          <button onClick={() => navigate('/profile')} className="icon-btn" title="Settings" aria-label="Settings">
            <FaCog size={14} />
          </button>

          {/* profile */}
          <div className="relative">
            <button
              onClick={() => {
                setShowUserMenu((s) => !s);
                setShowNotifMenu(false);
              }}
              className="icon-btn"
              title={user?.name || 'Leo Member'}
              style={{
                width: 'auto',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                padding: '0 8px 0 4px',
              }}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
            >
              <UserAvatar src={profileImage} size={28} verified ring="var(--header-bg)" fallback="/user.jpg" />
              <span
                className="profile-role hidden sm:inline"
                style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {user?.role || 'Club Member'}
              </span>
            </button>

            {showUserMenu && (
              <div
                role="menu"
                className="panel absolute right-0 mt-1 w-56 overflow-hidden z-[60]"
                style={{ boxShadow: '0 20px 50px -20px rgba(0,0,0,.7)' }}
              >
                <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                  <div className="text-xs font-semibold truncate">{user?.name || 'Leo Member'}</div>
                  <div className="truncate" style={{ fontSize: 11, color: 'var(--text-2)' }}>{user?.email || ''}</div>
                </div>

                <div className="p-2 text-xs">
                  <Link to="/profile" className="block px-2 py-1.5 rounded" style={{ borderRadius: 8 }}>
                    Your Profile
                  </Link>
                </div>

                <div className="px-3 py-2" style={{ fontSize: 11, color: 'var(--text-2)' }}>
                  Session: {showWarning ? <span className="num" style={{ color: 'var(--text-1)', fontWeight: 700 }}>{formatMs(timeright)}</span> : 'Active'}
                </div>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs"
                  style={{ color: 'var(--error)' }}
                >
                  <FaPowerOff className="h-3.5 w-3.5" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {showWarning && (
        <div className="fixed top-14 right-0 z-40">
          <div
            className="mx-3 md:mx-4 p-1.5 rounded-md text-[11px]"
            style={{ background: 'rgba(217,154,61,0.15)', color: 'var(--warning)', border: '1px solid rgba(217,154,61,0.3)' }}
          >
            Auto-logout in <span className="num font-semibold">{formatMs(timeright)}</span>.
          </div>
        </div>
      )}

    </>
  );
}
